import React, { useState, useEffect } from 'react';
import reportService from '../services/reportService';
import {
  HiOutlineChartBar,
  HiOutlineCalendar,
  HiOutlineCurrencyDollar,
  HiOutlineCube,
  HiOutlineDocumentText,
  HiOutlineUserGroup,
  HiOutlineRefresh,
  HiOutlineExclamationCircle,
  HiOutlinePrinter,
} from 'react-icons/hi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import './Reports.css';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [accountingData, setAccountingData] = useState(null);
  const [receivablesData, setReceivablesData] = useState(null);
  const [ncfData, setNcfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '' });

  const formatMoney = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });
  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const fetchReport = async (tab) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      switch (tab) {
        case 'sales': {
          const res = await reportService.getSales(params);
          setSalesData(res);
          break;
        }
        case 'inventory': {
          const res = await reportService.getInventory();
          setInventoryData(res);
          break;
        }
        case 'accounting': {
          const res = await reportService.getAccounting(params);
          setAccountingData(res);
          break;
        }
        case 'receivables': {
          const res = await reportService.getReceivables();
          setReceivablesData(res);
          break;
        }
        case 'ncf': {
          const res = await reportService.getNcf(params);
          setNcfData(res);
          break;
        }
        default: break;
      }
    } catch (err) {
      console.error('Error cargando reporte:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(activeTab); }, [activeTab]); // eslint-disable-line

  const handlePrintReport = () => {
    window.print();
  };

  const tabs = [
    { id: 'sales', label: 'Ventas', icon: <HiOutlineCurrencyDollar /> },
    { id: 'inventory', label: 'Inventario', icon: <HiOutlineCube /> },
    { id: 'accounting', label: 'Contabilidad', icon: <HiOutlineChartBar /> },
    { id: 'receivables', label: 'Cuentas x Cobrar', icon: <HiOutlineUserGroup /> },
    { id: 'ncf', label: 'NCF / DGII', icon: <HiOutlineDocumentText /> },
  ];

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h1>Reportes</h1>
          <p>Analíticas y métricas del negocio</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-icon" onClick={handlePrintReport} title="Imprimir Reporte">
            <HiOutlinePrinter />
          </button>
          <button className="btn-icon" onClick={() => fetchReport(activeTab)} title="Actualizar">
            <HiOutlineRefresh />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="expense-tabs" style={{ marginBottom: 16 }}>
        {tabs.map((t) => (
          <button key={t.id} className={`expense-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Date filters — no inventory */}
      {activeTab !== 'inventory' && (
        <div className="expense-filters" style={{ marginBottom: 20 }}>
          <label className="filter-label">
            Desde
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </label>
          <label className="filter-label">
            Hasta
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </label>
          <button className="btn-primary" onClick={() => fetchReport(activeTab)} style={{ alignSelf: 'flex-end' }}>Filtrar</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="loader"></div><p>Cargando reporte...</p></div>
      ) : (
        <>
          {/* ==================== VENTAS ==================== */}
          {activeTab === 'sales' && salesData && (
            <>
              <div className="report-metrics">
                <div className="report-metric">
                  <span className="metric-label">Ingresos Totales</span>
                  <span className="metric-value">RD$ {formatMoney(salesData.summary?.total_revenue)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Órdenes Completadas</span>
                  <span className="metric-value">{salesData.summary?.completed_orders || 0}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Promedio por Orden</span>
                  <span className="metric-value">RD$ {formatMoney(salesData.summary?.avg_order_value)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">ITBIS Cobrado</span>
                  <span className="metric-value">RD$ {formatMoney(salesData.summary?.total_tax)}</span>
                </div>
              </div>

              <div className="reports-grid">
                {/* Ventas por día */}
                <div className="report-card">
                  <h3><HiOutlineCalendar /> Ventas por Día</h3>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={(salesData.by_day || []).slice(0, 14).reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }}
                          tickFormatter={(v) => new Date(v).toLocaleDateString('es-DO', { day: '2-digit', month: 'short' })} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                        <Tooltip formatter={(v, name) => name === 'total' ? [`RD$ ${formatMoney(v)}`, 'Monto'] : [v, 'Órdenes']} />
                        <Bar dataKey="total" fill="#14532d" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Crédito vs Contado */}
                <div className="report-card">
                  <h3><HiOutlineChartBar /> Crédito vs Contado</h3>
                  <div className="chart-container pie-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={[
                          { name: 'Contado', value: Number(salesData.summary?.cash_total || 0), color: '#22c55e' },
                          { name: 'Crédito', value: Number(salesData.summary?.credit_total || 0), color: '#f59e0b' },
                        ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                          {[{ color: '#22c55e' }, { color: '#f59e0b' }].map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `RD$ ${formatMoney(v)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pie-legend">
                      <div className="legend-item"><span className="legend-dot" style={{ background: '#22c55e' }}></span>Contado: RD$ {formatMoney(salesData.summary?.cash_total)}</div>
                      <div className="legend-item"><span className="legend-dot" style={{ background: '#f59e0b' }}></span>Crédito: RD$ {formatMoney(salesData.summary?.credit_total)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top vendedores */}
              <div className="report-card">
                <h3>Top Vendedores</h3>
                <div className="top-salesmen">
                  {(salesData.by_salesman || []).map((s, i) => (
                    <div key={s.salesman} className="salesman-row">
                      <span className="salesman-rank">#{i + 1}</span>
                      <span className="salesman-name">{s.salesman}</span>
                      <span className="salesman-orders">{s.orders} órdenes</span>
                      <span className="salesman-total mono">RD$ {formatMoney(s.total)}</span>
                    </div>
                  ))}
                  {(salesData.by_salesman || []).length === 0 && <p className="no-data">Sin datos</p>}
                </div>
              </div>

              {/* Top productos */}
              <div className="report-card">
                <h3>Productos Más Vendidos</h3>
                {(salesData.top_products || []).length > 0 ? (
                  <table className="data-table">
                    <thead><tr><th>#</th><th>Producto</th><th>SKU</th><th>Unidades</th><th>Total Vendido</th></tr></thead>
                    <tbody>
                      {salesData.top_products.map((p, i) => (
                        <tr key={p.sku}>
                          <td>#{i + 1}</td>
                          <td>{p.name}</td>
                          <td className="mono-cell">{p.sku}</td>
                          <td className="center">{Number(p.total_qty).toLocaleString()}</td>
                          <td className="mono-cell">RD$ {formatMoney(p.total_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="no-data">Sin datos</p>}
              </div>

              {/* Top clientes */}
              <div className="report-card">
                <h3>Mejores Clientes</h3>
                {(salesData.by_client || []).length > 0 ? (
                  <table className="data-table">
                    <thead><tr><th>#</th><th>Cliente</th><th>Órdenes</th><th>Total Compras</th></tr></thead>
                    <tbody>
                      {salesData.by_client.map((c, i) => (
                        <tr key={i}>
                          <td>#{i + 1}</td>
                          <td>{c.client || c.contact_name}</td>
                          <td className="center">{c.orders}</td>
                          <td className="mono-cell">RD$ {formatMoney(c.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="no-data">Sin datos</p>}
              </div>
            </>
          )}

          {/* ==================== INVENTARIO ==================== */}
          {activeTab === 'inventory' && inventoryData && (
            <>
              <div className="report-metrics">
                <div className="report-metric">
                  <span className="metric-label">Total Productos</span>
                  <span className="metric-value">{inventoryData.summary?.total_products || 0}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Valor del Inventario</span>
                  <span className="metric-value">RD$ {formatMoney(inventoryData.summary?.total_inventory_value)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Potencial de Venta</span>
                  <span className="metric-value">RD$ {formatMoney(inventoryData.summary?.total_potential_revenue)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label" style={{ color: '#ef4444' }}>Stock Bajo / Agotado</span>
                  <span className="metric-value" style={{ color: '#ef4444' }}>
                    {inventoryData.summary?.low_stock_count || 0} / {inventoryData.summary?.out_of_stock_count || 0}
                  </span>
                </div>
              </div>

              {/* Alertas de stock bajo */}
              {(inventoryData.low_stock || []).length > 0 && (
                <div className="report-card" style={{ borderLeft: '4px solid #ef4444' }}>
                  <h3><HiOutlineExclamationCircle style={{ color: '#ef4444' }} /> Productos con Stock Bajo</h3>
                  <table className="data-table">
                    <thead><tr><th>Producto</th><th>SKU</th><th>Stock</th><th>Mínimo</th><th>Valor</th></tr></thead>
                    <tbody>
                      {inventoryData.low_stock.map((p) => (
                        <tr key={p.sku} style={p.stock === 0 ? { background: '#fef2f2' } : {}}>
                          <td>{p.name}</td>
                          <td className="mono-cell">{p.sku}</td>
                          <td className="center" style={{ color: p.stock === 0 ? '#ef4444' : '#f59e0b', fontWeight: 700 }}>{p.stock} {p.unit}</td>
                          <td className="center">{p.minimum_threshold}</td>
                          <td className="mono-cell">RD$ {formatMoney(p.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Valor por categoría */}
              <div className="report-card">
                <h3>Valor por Categoría</h3>
                <table className="data-table">
                  <thead><tr><th>Categoría</th><th>Productos</th><th>Unidades</th><th>Valor (costo)</th></tr></thead>
                  <tbody>
                    {(inventoryData.by_category || []).map((c) => (
                      <tr key={c.category}>
                        <td>{c.category}</td>
                        <td className="center">{c.products}</td>
                        <td className="center">{Number(c.total_units).toLocaleString()}</td>
                        <td className="mono-cell">RD$ {formatMoney(c.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Rotación de inventario */}
              <div className="report-card">
                <h3>Rotación de Inventario (últimos 30 días)</h3>
                <table className="data-table">
                  <thead><tr><th>Producto</th><th>Stock</th><th>Vendido (30d)</th><th>Días de Stock</th></tr></thead>
                  <tbody>
                    {(inventoryData.turnover || []).filter(t => Number(t.sold_30d) > 0).map((t) => (
                      <tr key={t.sku}>
                        <td>{t.name}</td>
                        <td className="center">{t.stock}</td>
                        <td className="center">{Number(t.sold_30d).toLocaleString()}</td>
                        <td className="center" style={{ fontWeight: 600, color: t.days_of_stock && t.days_of_stock < 7 ? '#ef4444' : '#16a34a' }}>
                          {t.days_of_stock ? `${t.days_of_stock} días` : '∞'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ==================== CONTABILIDAD ==================== */}
          {activeTab === 'accounting' && accountingData && (
            <>
              <div className="report-metrics">
                <div className="report-metric">
                  <span className="metric-label">Ingresos</span>
                  <span className="metric-value" style={{ color: '#16a34a' }}>RD$ {formatMoney(accountingData.result?.total_revenue)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Gastos</span>
                  <span className="metric-value" style={{ color: '#ef4444' }}>RD$ {formatMoney(accountingData.result?.total_expenses)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Resultado Neto</span>
                  <span className="metric-value" style={{ color: accountingData.result?.net_income >= 0 ? '#16a34a' : '#ef4444' }}>
                    RD$ {formatMoney(accountingData.result?.net_income)}
                  </span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Margen</span>
                  <span className="metric-value">{accountingData.result?.margin}%</span>
                </div>
              </div>

              <div className="detail-grid">
                {/* Ingresos */}
                <div className="detail-card">
                  <h3 style={{ color: '#16a34a' }}>Ingresos</h3>
                  <div className="info-grid">
                    <div className="info-row"><span className="info-label">Subtotal</span><span className="info-value mono">RD$ {formatMoney(accountingData.revenue?.subtotal)}</span></div>
                    <div className="info-row"><span className="info-label">ITBIS cobrado</span><span className="info-value mono">RD$ {formatMoney(accountingData.revenue?.tax_collected)}</span></div>
                    <div className="info-row"><span className="info-label">Total</span><span className="info-value mono" style={{ fontWeight: 700 }}>RD$ {formatMoney(accountingData.revenue?.total_revenue)}</span></div>
                    <div className="info-row"><span className="info-label">Ventas contado</span><span className="info-value mono">RD$ {formatMoney(accountingData.revenue?.cash_sales)}</span></div>
                    <div className="info-row"><span className="info-label">Ventas crédito</span><span className="info-value mono">RD$ {formatMoney(accountingData.revenue?.credit_sales)}</span></div>
                    <div className="info-row"><span className="info-label">Órdenes</span><span className="info-value">{accountingData.revenue?.order_count || 0}</span></div>
                  </div>
                </div>

                {/* Gastos */}
                <div className="detail-card">
                  <h3 style={{ color: '#ef4444' }}>Gastos por Categoría</h3>
                  <div className="info-grid">
                    {(accountingData.expenses?.by_category || []).map((c) => (
                      <div className="info-row" key={c.category}>
                        <span className="info-label">{c.category}</span>
                        <span className="info-value mono" style={{ color: '#ef4444' }}>RD$ {formatMoney(c.total)}</span>
                      </div>
                    ))}
                    <div className="info-row" style={{ borderTop: '2px solid #e2e8f0', paddingTop: 8 }}>
                      <span className="info-label" style={{ fontWeight: 700 }}>Total Gastos</span>
                      <span className="info-value mono" style={{ fontWeight: 700, color: '#ef4444' }}>RD$ {formatMoney(accountingData.expenses?.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Comisiones */}
                <div className="detail-card">
                  <h3>Comisiones</h3>
                  <div className="info-grid">
                    <div className="info-row"><span className="info-label">Total generado</span><span className="info-value mono">RD$ {formatMoney(accountingData.commissions?.total)}</span></div>
                    <div className="info-row"><span className="info-label">Pagado</span><span className="info-value mono" style={{ color: '#16a34a' }}>RD$ {formatMoney(accountingData.commissions?.paid)}</span></div>
                    <div className="info-row"><span className="info-label">Pendiente</span><span className="info-value mono" style={{ color: '#f59e0b' }}>RD$ {formatMoney(accountingData.commissions?.pending)}</span></div>
                  </div>
                </div>

                {/* Cuentas por cobrar */}
                <div className="detail-card">
                  <h3>Cuentas por Cobrar</h3>
                  <div className="info-grid">
                    <div className="info-row"><span className="info-label">Facturas pendientes</span><span className="info-value">{accountingData.receivables?.count || 0}</span></div>
                    <div className="info-row"><span className="info-label">Total por cobrar</span><span className="info-value mono" style={{ fontWeight: 700, color: '#f59e0b' }}>RD$ {formatMoney(accountingData.receivables?.total)}</span></div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ==================== CUENTAS POR COBRAR ==================== */}
          {activeTab === 'receivables' && receivablesData && (
            <>
              <div className="report-metrics">
                <div className="report-metric">
                  <span className="metric-label">Total por Cobrar</span>
                  <span className="metric-value" style={{ color: '#f59e0b' }}>RD$ {formatMoney(receivablesData.total)}</span>
                </div>
                <div className="report-metric">
                  <span className="metric-label">Facturas Pendientes</span>
                  <span className="metric-value">{receivablesData.count || 0}</span>
                </div>
              </div>

              {/* Por cliente */}
              <div className="report-card">
                <h3>Deuda por Cliente</h3>
                {(receivablesData.by_client || []).length > 0 ? (
                  <table className="data-table">
                    <thead><tr><th>Cliente</th><th>RNC</th><th>Facturas</th><th>Más Antigua</th><th>Total Adeudado</th></tr></thead>
                    <tbody>
                      {receivablesData.by_client.map((c, i) => (
                        <tr key={i}>
                          <td>{c.client || c.contact_name}</td>
                          <td className="mono-cell">{c.rnc || '-'}</td>
                          <td className="center">{c.invoices}</td>
                          <td className="date-cell">{formatDate(c.oldest_invoice)}</td>
                          <td className="mono-cell total-cell" style={{ color: '#f59e0b' }}>RD$ {formatMoney(c.total_owed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="no-data">No hay cuentas por cobrar</p>}
              </div>

              {/* Detalle de facturas */}
              {(receivablesData.items || []).length > 0 && (
                <div className="report-card">
                  <h3>Detalle de Facturas Pendientes</h3>
                  <table className="data-table">
                    <thead><tr><th>Orden</th><th>Cliente</th><th>Vendedor</th><th>Fecha</th><th>Días</th><th>Monto</th></tr></thead>
                    <tbody>
                      {receivablesData.items.map((r) => (
                        <tr key={r.id} style={Number(r.days_outstanding) > 30 ? { background: '#fef2f2' } : {}}>
                          <td className="mono-cell">{r.order_number}</td>
                          <td>{r.client || r.contact_name}</td>
                          <td>{r.salesman}</td>
                          <td className="date-cell">{formatDate(r.closed_at)}</td>
                          <td className="center" style={{ fontWeight: 600, color: Number(r.days_outstanding) > 30 ? '#ef4444' : '#64748b' }}>
                            {Math.round(r.days_outstanding || 0)}d
                          </td>
                          <td className="mono-cell total-cell">RD$ {formatMoney(r.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ==================== NCF / DGII ==================== */}
          {activeTab === 'ncf' && ncfData && (
            <>
              <div className="report-metrics">
                <div className="report-metric">
                  <span className="metric-label">Comprobantes Emitidos</span>
                  <span className="metric-value">{ncfData.total_count || 0}</span>
                </div>
              </div>

              {/* Resumen por tipo */}
              {(ncfData.by_type || []).length > 0 && (
                <div className="report-card">
                  <h3>Resumen por Tipo NCF</h3>
                  <table className="data-table">
                    <thead><tr><th>Tipo</th><th>Cantidad</th><th>Subtotal</th><th>ITBIS</th><th>Total</th></tr></thead>
                    <tbody>
                      {ncfData.by_type.map((t) => (
                        <tr key={t.ncf_type}>
                          <td className="mono-cell">{t.ncf_type}</td>
                          <td className="center">{t.count}</td>
                          <td className="mono-cell">RD$ {formatMoney(t.subtotal)}</td>
                          <td className="mono-cell">RD$ {formatMoney(t.tax)}</td>
                          <td className="mono-cell total-cell">RD$ {formatMoney(t.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Listado de NCF emitidos */}
              <div className="report-card">
                <h3>Comprobantes Emitidos</h3>
                {(ncfData.invoices || []).length > 0 ? (
                  <table className="data-table">
                    <thead><tr><th>NCF</th><th>Tipo</th><th>Orden</th><th>Cliente</th><th>RNC</th><th>Fecha</th><th>Total</th></tr></thead>
                    <tbody>
                      {ncfData.invoices.map((inv) => (
                        <tr key={inv.ncf_number}>
                          <td className="mono-cell" style={{ fontWeight: 600 }}>{inv.ncf_number}</td>
                          <td className="mono-cell">{inv.ncf_type}</td>
                          <td className="mono-cell">{inv.order_number}</td>
                          <td>{inv.client || inv.contact_name}</td>
                          <td className="mono-cell">{inv.rnc || '-'}</td>
                          <td className="date-cell">{formatDate(inv.invoice_date)}</td>
                          <td className="mono-cell total-cell">RD$ {formatMoney(inv.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="no-data">No hay comprobantes emitidos</p>}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
